import { DISCOVERY_LAB_ASSETS } from '../DiscoveryLabAssets.js';
import {
  calculateDiscoveryLabSafeBounds,
  isCompactDiscoveryLabLayout,
} from '../components/DiscoveryLabLayout.js';
import { ErosionInvestigation } from './ErosionInvestigation.js';
import type { ErosionTestConditionId } from './ErosionInvestigationConfig.js';
import type { ErosionInvestigationSnapshot, ErosionTestRecord } from './ErosionInvestigationState.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface ErosionInvestigationViewOptions {
  readonly reducedMotion: boolean;
  readonly onExit: () => void;
  readonly onComplete: () => void;
}

interface PanelButton {
  readonly container: Phaser.GameObjects.Container;
  readonly frame: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

/**
 * Production-art presentation adapter for the UI-independent erosion engine.
 * The engine owns all curriculum state; rebuilding this view on resize never
 * resets an observation, prediction, or test run.
 */
export class ErosionInvestigationView {
  private readonly root: Phaser.GameObjects.Container;
  private readonly background: Phaser.GameObjects.Image;
  private phaseLayer?: Phaser.GameObjects.Container;
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private suspended = false;
  private inputLocked = false;
  private completionQueued = false;
  private destroyed = false;
  private hintPhase?: ErosionInvestigationSnapshot['phase'];
  private hintStep = 0;
  private hintMessage = '';

  constructor(
    private readonly scene: Phaser.Scene,
    readonly investigation: ErosionInvestigation,
    private readonly options: ErosionInvestigationViewOptions,
  ) {
    this.root = scene.add.container(0, 0).setDepth(220);
    this.background = scene.add.image(
      640,
      360,
      DISCOVERY_LAB_ASSETS.erosionStation.key,
    ).setDisplaySize(1280, 720);
    this.root.add(this.background);
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.render();
  }

  layout() {
    if (!this.destroyed) this.render();
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.scene.tweens.killTweensOf(this.root.getAll());
    this.root.destroy(true);
  }

  private render() {
    this.phaseLayer?.destroy(true);
    this.phaseLayer = this.scene.add.container(0, 0);
    this.root.add(this.phaseLayer);

    const snapshot = this.investigation.getSnapshot();
    if (this.hintPhase !== snapshot.phase) {
      this.hintPhase = snapshot.phase;
      this.hintStep = 0;
      this.hintMessage = '';
    }
    if (snapshot.phase === 'observe') this.renderObserve();
    else if (snapshot.phase === 'predict') this.renderPredict();
    else if (snapshot.phase === 'test') this.renderTest(snapshot);
    else if (snapshot.phase === 'compare') this.renderCompare(snapshot);
    else this.renderComplete();
  }

  private renderObserve() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('OBSERVE', 'Washed-Out Trail Investigation');

    const trail = this.scene.add.image(
      safe.centerX - (compact ? 190 : 230),
      safe.centerY + 12,
      DISCOVERY_LAB_ASSETS.trailWashout.key,
    ).setDisplaySize(compact ? 430 : 500, compact ? 242 : 281);
    this.phaseLayer!.add(trail);

    this.createInfoPanel(
      safe.centerX + (compact ? 240 : 280),
      safe.centerY - 12,
      compact ? 430 : 500,
      compact ? 210 : 238,
      'What do you notice?',
      this.investigation.config.sceneObservation,
      compact ? 15 : 17,
    );
    const button = this.createPanelButton(
      safe.centerX + (compact ? 240 : 280),
      safe.bottom - 76,
      compact ? 350 : 390,
      compact ? 66 : 72,
      'LOG OBSERVATION  →',
      () => {
        if (this.canInteract() && this.investigation.acknowledgeObservation()) this.render();
      },
    );
    this.phaseLayer!.add(button.container);
  }

  private renderPredict() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('PREDICT', this.investigation.config.investigationQuestion);

    const optionWidth = Math.min(compact ? 310 : 345, (safe.width - 80) / 3);
    const gap = optionWidth + (compact ? 16 : 24);
    const centerY = safe.centerY + 20;
    this.investigation.config.predictionOptions.forEach((option, index) => {
      const x = safe.centerX + (index - 1) * gap;
      const trailTexture = index === 0
        ? DISCOVERY_LAB_ASSETS.trailErosion.key
        : index === 1
          ? DISCOVERY_LAB_ASSETS.trailPartial.key
          : DISCOVERY_LAB_ASSETS.trailDry.key;
      const preview = this.scene.add.image(x, centerY - 68, trailTexture)
        .setDisplaySize(optionWidth, optionWidth * 0.5625)
        .setInteractive({ useHandCursor: true });
      const button = this.createPanelButton(
        x,
        centerY + (compact ? 85 : 102),
        optionWidth,
        compact ? 118 : 130,
        `${index + 1}. ${option.label}`,
        () => this.choosePrediction(option.id),
        compact ? 13 : 15,
      );
      preview.on('pointerup', () => this.choosePrediction(option.id));
      this.phaseLayer!.add([preview, button.container]);
    });
  }

  private renderTest(snapshot: ErosionInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('TEST', this.investigation.config.testConfig.variablePrompt);

    const trayX = safe.centerX - (compact ? 195 : 245);
    const trayY = safe.centerY + 42;
    const tray = this.scene.add.image(trayX, trayY, DISCOVERY_LAB_ASSETS.siftTray.key)
      .setCrop(190, 120, 420, 470)
      .setDisplaySize(compact ? 300 : 350, compact ? 336 : 392);
    const water = this.scene.add.image(trayX + (compact ? 112 : 132), trayY - (compact ? 64 : 76), DISCOVERY_LAB_ASSETS.waterSample.key)
      .setCrop(250, 120, 250, 520)
      .setDisplaySize(compact ? 100 : 116, compact ? 208 : 242).setAngle(-18);
    this.phaseLayer!.add([tray, water]);

    const controlsX = safe.centerX + (compact ? 250 : 292);
    const runIds = new Set(snapshot.testRecords.map(record => record.conditionId));
    const conditions = this.investigation.config.testConfig.conditions;
    conditions.forEach((condition, index) => {
      const complete = runIds.has(condition.id);
      const selected = snapshot.selectedConditionId === condition.id;
      const button = this.createPanelButton(
        controlsX,
        safe.centerY - (compact ? 116 : 132) + index * (compact ? 100 : 112),
        compact ? 405 : 455,
        compact ? 78 : 88,
        `${index + 1}. ${condition.label}${complete ? '  ✓ TESTED' : ''}`,
        () => this.selectCondition(condition.id),
        compact ? 15 : 17,
      );
      if (selected) button.frame.setTint(0x72f4ff);
      if (complete) button.container.setAlpha(0.62);
      this.phaseLayer!.add(button.container);
    });

    const selected = snapshot.selectedConditionId !== undefined;
    const runButton = this.createPanelButton(
      controlsX,
      safe.centerY + (compact ? 124 : 144),
      compact ? 330 : 370,
      compact ? 67 : 74,
      selected ? 'RUN WATER MODEL' : 'CHOOSE A FLOW',
      () => this.runTest(),
      compact ? 15 : 17,
    );
    runButton.container.setAlpha(selected ? 1 : 0.55);
    this.phaseLayer!.add(runButton.container);

    const records = snapshot.testRecords.map(record => record.result.observation).join('\n');
    const progress = records || 'Keep the tray, soil, slope, and test time the same. Change only water-flow strength.';
    const feedback = this.makeText(progress, compact ? 13 : 15, '#efffff', 'bold')
      .setPosition(safe.centerX, safe.bottom - 36)
      .setWordWrapWidth(Math.min(920, safe.width - 90));
    this.phaseLayer!.add(feedback);
  }

  private renderCompare(snapshot: ErosionInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('COMPARE', this.investigation.config.comparisonPrompt);

    const width = compact ? 400 : 455;
    const height = width * 0.5625;
    const offset = compact ? 225 : 260;
    const centerY = safe.centerY - 15;
    const gentle = this.scene.add.image(safe.centerX - offset, centerY, DISCOVERY_LAB_ASSETS.trailPartial.key)
      .setDisplaySize(width, height);
    const strong = this.scene.add.image(safe.centerX + offset, centerY, DISCOVERY_LAB_ASSETS.trailErosion.key)
      .setDisplaySize(width, height);
    const gentleLabel = this.makeText('GENTLE FLOW\nA little soil moved • shallow path', compact ? 14 : 16, '#ffffff', 'bold')
      .setPosition(gentle.x, centerY + height * 0.5 + 31);
    const strongLabel = this.makeText('STRONG FLOW\nMore soil moved • deeper path', compact ? 14 : 16, '#ffffff', 'bold')
      .setPosition(strong.x, centerY + height * 0.5 + 31);
    this.phaseLayer!.add([gentle, strong, gentleLabel, strongLabel]);

    const prediction = this.investigation.config.predictionOptions.find(
      option => option.id === snapshot.predictionId,
    );
    const evidence = this.makeText(
      `Your prediction: ${prediction?.label ?? ''}`,
      compact ? 13 : 15,
      '#dffcff',
      'bold',
    ).setPosition(safe.centerX, safe.bottom - 112)
      .setWordWrapWidth(Math.min(880, safe.width - 100));
    const apply = this.createPanelButton(
      safe.centerX,
      safe.bottom - 54,
      compact ? 420 : 475,
      compact ? 66 : 72,
      'APPLY THE FINDING TO THE TRAIL',
      () => this.applyFinding(),
      compact ? 14 : 16,
    );
    this.phaseLayer!.add([evidence, apply.container]);
  }

  private renderComplete() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('DISCOVERY APPLIED', 'Stronger flowing water can move more loose soil.');
    const restored = this.scene.add.image(
      safe.centerX,
      safe.centerY + 10,
      DISCOVERY_LAB_ASSETS.trailRestored.key,
    ).setDisplaySize(compact ? 720 : 810, compact ? 405 : 456);
    const message = this.makeText(
      'The first trail protection stage is in place. The field campus has changed!',
      compact ? 16 : 19,
      '#ffffff',
      'bold',
    ).setPosition(safe.centerX, safe.bottom - 50)
      .setWordWrapWidth(Math.min(820, safe.width - 110));
    const vfx = this.scene.add.image(
      safe.centerX,
      safe.centerY,
      DISCOVERY_LAB_ASSETS.restorationVfx.key,
    ).setDisplaySize(compact ? 360 : 430, compact ? 388 : 463)
      .setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    this.phaseLayer!.add([restored, vfx, message]);
    this.phaseLayer!.bringToTop(vfx);

    if (!this.completionQueued) {
      this.completionQueued = true;
      if (this.options.reducedMotion) {
        vfx.setAlpha(0.6);
      } else {
        vfx.setScale(vfx.scaleX * 0.75, vfx.scaleY * 0.75);
        this.scene.tweens.add({
          targets: vfx,
          alpha: 0.72,
          scaleX: vfx.scaleX / 0.75,
          scaleY: vfx.scaleY / 0.75,
          duration: 520,
          ease: 'Back.easeOut',
        });
      }
      this.timers.push(this.scene.time.delayedCall(this.options.reducedMotion ? 800 : 1500, () => {
        if (!this.destroyed) this.options.onComplete();
      }));
    }
  }

  private createHeader(phase: string, detail: string) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const panel = this.scene.add.image(
      safe.centerX,
      safe.y + (compact ? 67 : 75),
      DISCOVERY_LAB_ASSETS.fieldJournalPlaque.key,
    ).setDisplaySize(Math.min(compact ? 600 : 680, safe.width - 260), compact ? 128 : 145);
    const phaseText = this.makeText(phase, compact ? 17 : 20, '#fff8df', 'bold')
      .setPosition(safe.centerX, panel.y - (compact ? 22 : 25));
    const detailText = this.makeText(this.hintMessage || detail, compact ? 13 : 15, '#ffffff', 'bold')
      .setPosition(safe.centerX, panel.y + (compact ? 25 : 29))
      .setWordWrapWidth(panel.displayWidth - 190);
    const close = this.createPanelButton(
      safe.x + (compact ? 58 : 66),
      safe.y + (compact ? 48 : 54),
      compact ? 104 : 118,
      compact ? 54 : 60,
      '← EXIT',
      () => {
        if (this.canInteract()) this.options.onExit();
      },
      compact ? 12 : 13,
    );
    this.phaseLayer!.add([panel, phaseText, detailText, close.container]);
    if (this.investigation.getSnapshot().phase !== 'complete') {
      const hint = this.createPanelButton(
        safe.right - (compact ? 58 : 66),
        safe.y + (compact ? 48 : 54),
        compact ? 104 : 118,
        compact ? 54 : 60,
        'HINT  H',
        () => this.showHint(),
        compact ? 12 : 13,
      );
      this.phaseLayer!.add(hint.container);
    }
  }

  private createInfoPanel(
    x: number,
    y: number,
    width: number,
    height: number,
    heading: string,
    body: string,
    bodySize: number,
  ) {
    const panel = this.scene.add.image(x, y, DISCOVERY_LAB_ASSETS.fieldJournalPlaque.key)
      .setDisplaySize(width, height);
    const title = this.makeText(heading, bodySize + 3, '#fff8df', 'bold')
      .setPosition(x, y - height * 0.25);
    const copy = this.makeText(body, bodySize, '#ffffff', 'bold')
      .setPosition(x, y + height * 0.08)
      .setWordWrapWidth(width - 100);
    this.phaseLayer!.add([panel, title, copy]);
  }

  private createPanelButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    action: () => void,
    fontSize = 16,
  ): PanelButton {
    const container = this.scene.add.container(x, y);
    const frame = this.scene.add.image(0, 0, DISCOVERY_LAB_ASSETS.destinationSignpost.key)
      .setDisplaySize(width, Math.max(height, width / 4.6))
      .setInteractive({ useHandCursor: true });
    const text = this.makeText(label, fontSize, '#ffffff', 'bold')
      .setWordWrapWidth(width - 64);
    frame.on('pointerdown', () => {
      if (this.canInteract()) container.setAlpha(0.78);
    });
    frame.on('pointerout', () => container.setAlpha(1));
    frame.on('pointerup', () => {
      container.setAlpha(1);
      if (this.canInteract()) action();
    });
    container.add([frame, text]);
    return { container, frame, label: text };
  }

  private makeText(
    value: string,
    size: number,
    color: string,
    fontStyle: 'normal' | 'bold',
  ) {
    return this.scene.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle,
      color,
      align: 'center',
      lineSpacing: 3,
      stroke: '#062b45',
      strokeThickness: 4,
    }).setOrigin(0.5).setResolution(2);
  }

  private choosePrediction(id: string) {
    if (this.canInteract() && this.investigation.choosePrediction(id)) this.render();
  }

  private selectCondition(id: ErosionTestConditionId) {
    if (this.canInteract() && this.investigation.selectCondition(id)) this.render();
  }

  private runTest() {
    if (!this.canInteract()) return;
    const record = this.investigation.runSelectedCondition();
    if (!record) return;
    this.inputLocked = true;
    this.playTestResult(record);
  }

  private playTestResult(record: ErosionTestRecord) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const texture = record.conditionId === 'strong-flow'
      ? DISCOVERY_LAB_ASSETS.erosionParticles.key
      : DISCOVERY_LAB_ASSETS.waterSplashVfx.key;
    const effect = this.scene.add.image(
      safe.centerX - (compact ? 190 : 235),
      safe.centerY + 32,
      texture,
    ).setDisplaySize(compact ? 260 : 310, compact ? 280 : 334)
      .setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    this.phaseLayer!.add(effect);
    if (this.options.reducedMotion) {
      effect.setAlpha(0.62);
    } else {
      const finalX = effect.scaleX;
      const finalY = effect.scaleY;
      effect.setScale(finalX * 0.75, finalY * 0.75);
      this.scene.tweens.add({
        targets: effect,
        alpha: 0.72,
        scaleX: finalX,
        scaleY: finalY,
        duration: 430,
        ease: 'Sine.easeOut',
      });
    }
    this.timers.push(this.scene.time.delayedCall(this.options.reducedMotion ? 450 : 900, () => {
      this.inputLocked = false;
      if (!this.destroyed) this.render();
    }));
  }

  private applyFinding() {
    if (!this.canInteract()) return;
    this.inputLocked = true;
    const result = this.investigation.compareResults();
    if (!result) {
      this.inputLocked = false;
      return;
    }
    this.render();
  }

  private showHint() {
    if (!this.canInteract()) return;
    const snapshot = this.investigation.getSnapshot();
    if (snapshot.phase === 'complete') return;
    const hints: Record<Exclude<ErosionInvestigationSnapshot['phase'], 'complete'>, readonly string[]> = {
      observe: [
        'Look for cloudy water, shifted soil, and the groove crossing the trail.',
        'Those are clues that moving water changed the land.',
      ],
      predict: [
        'Think about which flow can push more loose soil.',
        'A prediction is your best idea before you run both fair tests.',
      ],
      test: [
        'Change only water-flow strength. Keep the tray, soil, slope, and time the same.',
        snapshot.testRecords.length === 0
          ? 'Choose either flow first, then run the other flow for comparison.'
          : 'One flow is tested. Choose the other flow next.',
      ],
      compare: [
        'Compare how much soil moved and how deep each path became.',
        'Use what happened in both tests—not only your prediction—as evidence.',
      ],
    };
    const phaseHints = hints[snapshot.phase];
    this.hintMessage = phaseHints[Math.min(this.hintStep, phaseHints.length - 1)];
    this.hintStep = Math.min(this.hintStep + 1, phaseHints.length - 1);
    this.render();
  }

  private canInteract() {
    return !this.destroyed && !this.suspended && !this.inputLocked;
  }

  private readonly handleKeyDown = (event: KeyboardEvent) => {
    if (!this.canInteract()) return;
    const snapshot = this.investigation.getSnapshot();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.options.onExit();
      return;
    }
    if (event.key.toLowerCase() === 'h') {
      event.preventDefault();
      this.showHint();
      return;
    }
    if (snapshot.phase === 'observe' && (event.key === 'Enter' || event.key === ' ' || event.key.toLowerCase() === 'o')) {
      event.preventDefault();
      if (this.investigation.acknowledgeObservation()) this.render();
      return;
    }
    if (snapshot.phase === 'predict' && event.key >= '1' && event.key <= '3') {
      event.preventDefault();
      const option = this.investigation.config.predictionOptions[Number(event.key) - 1];
      if (option) this.choosePrediction(option.id);
      return;
    }
    if (snapshot.phase === 'test') {
      if (event.key === '1' || event.key.toLowerCase() === 'g') {
        event.preventDefault();
        this.selectCondition('gentle-flow');
      } else if (event.key === '2' || event.key.toLowerCase() === 's') {
        event.preventDefault();
        this.selectCondition('strong-flow');
      } else if (event.key === 'Enter' || event.key === ' ' || event.key.toLowerCase() === 'r') {
        event.preventDefault();
        this.runTest();
      }
      return;
    }
    if (snapshot.phase === 'compare' && (event.key === 'Enter' || event.key === ' ' || event.key.toLowerCase() === 'a')) {
      event.preventDefault();
      this.applyFinding();
    }
  };
}
